import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { load } from 'recaptcha-v3'
import RecaptchaV2 from 'react-google-recaptcha'
import {
  Form,
  Modal,
  Level,
  Box,
  Heading,
  Toasts,
  Button,
  Toolbar,
  Icon,
} from '@deseretbook/react-ui'
import { DeseretBook } from '../../../dbcom/api'
import {
  AddressFormManager,
  AddressFormFields,
  PaymentFormFields,
  PaymentFormManager,
} from '../../forms'
import RecaptchaDisclaimer from '../../RecaptchaDisclaimer'
import { FullError } from '../../../errors'

// Translation between braintree card types and icon names.
const cardIcons = {
  'american express': 'ccAmex',
  mastercard: 'ccMastercard',
  visa: 'ccVisa',
  discover: 'ccDiscover',
}

/**
 * New Card View of the subscription signup modal.
 *
 * @returns {function} Component
 */
const NewCardView = ({ user, onSuccess, changeView }) => {
  const [addressForm, setAddressForm] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCheckboxRecaptcha, setShowCheckboxRecaptcha] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState(null)

  const siteKeyV2 = window.__GLOBALS__.config.recaptchaSiteKeyV2

  /**
   * Updates the address form with the given changes.
   *
   * @param {object} [changes={}] - Changes to address
   */
  const updateAddress = (changes = {}) => {
    setAddressForm((prevAddress) => ({ ...prevAddress, ...changes }))
  }

  /**
   * Sets the recaptcha token if the recaptcha checkbox is present
   *
   * @param {string} token Recaptcha token returned by recaptcha component
   */
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token)
  }

  /**
   * Saves the user's address to their address book.
   *
   * @param {object} hostedFields - HostedFields data
   */
  const saveAddress = (hostedFields) => {
    setIsSubmitting(true)

    hostedFields.tokenize(async (tokenizeError, payload) => {
      try {
        if (tokenizeError) {
          throw new Error(tokenizeError)
        }

        let newCardRecaptchaToken = recaptchaToken

            // Use V3 recaptcha if checkbox is false and the token isn't present
            if (!showCheckboxRecaptcha && !newCardRecaptchaToken) {
              /* eslint-disable-next-line no-underscore-dangle */
              const siteKeyV3 = window.__GLOBALS__.config.recaptchaSiteKeyV3

              if (siteKeyV3) {
                const reCaptcha = await load(siteKeyV3)
                
                newCardRecaptchaToken = await reCaptcha.execute(
                  'credit_cards/create',
                )
              }
            }

        const newCard = await DeseretBook.user(user)
          .creditCard()
          .create({
            cardholderName: `${addressForm.firstname} ${addressForm.lastname}`,
            paymentMethodNonce: payload.nonce,
            billingAddress: addressForm,
            recaptchaToken: newCardRecaptchaToken,
          })

        if (newCard.error) {
          throw new FullError(newCard)
        }

        setIsSubmitting(false)
        Toasts.success('Card saved')

        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(newCard)
        }
      } catch (error) {
        setIsSubmitting(false)

        setRecaptchaToken(null)

        if (error.errors.includes('Failed Recaptcha')) {
          setShowCheckboxRecaptcha(true)
        }

        Toasts.danger('Unable to save', (error && error.message) || '')
      }
    })
  }

  return (
    <AddressFormManager address={addressForm} onChange={updateAddress}>
      {/*
        TODO I think paymentMethod defaults to the Spree::Card or whatever, but we should send the
        real thing in probably
      */}
      {(addressFormProps) => (
        <PaymentFormManager paymentMethod={{}}>
          {(paymentFormProps) => {
            const { hostedFields, currentCard } = paymentFormProps
            const isFetchingData =
              addressFormProps.isFetchingData || paymentFormProps.isFetchingData

            return (
              <React.Fragment>
                <Modal.Header>
                  <Toolbar
                    titleContent="Create New Card"
                    figureContent={
                      <Icon
                        name={
                          cardIcons[currentCard && currentCard.niceType.toLowerCase()] ||
                          'ccGeneric'
                        }
                      />
                    }
                  />
                </Modal.Header>
                <Modal.Content>
                  <Form
                    id="newcard-form"
                    onSubmit={() => saveAddress(hostedFields)}
                    isSubmitting={isSubmitting}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <PaymentFormFields {...paymentFormProps} />
                    <Box paddingVertical={1}>
                      <Heading>Billing Address</Heading>
                    </Box>
                    <AddressFormFields {...addressFormProps} />
                    {showCheckboxRecaptcha && siteKeyV2 && (
                      <RecaptchaV2
                        sitekey={siteKeyV2}
                        onChange={handleRecaptchaChange}
                        onExpired={() => {
                          setRecaptchaToken(null)
                        }}
                      />
                    )}
                  </Form>
                </Modal.Content>
                <Modal.Footer>
                  <Level>
                    <Level.Item>
                      <RecaptchaDisclaimer />
                    </Level.Item>
                    <Level.Right>
                      <Level.Item>
                        <Button
                          type="button"
                          onClick={() => changeView('form')}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </Level.Item>
                      <Level.Item>
                        <Button
                          form="newcard-form"
                          type="submit"
                          color="action"
                          isLoading={isFetchingData}
                          disabled={
                            isFetchingData ||
                            (showCheckboxRecaptcha && !recaptchaToken)
                          }
                        >
                          Create New Card
                        </Button>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Modal.Footer>
              </React.Fragment>
            )
          }}
        </PaymentFormManager>
      )}
    </AddressFormManager>
  )
}

NewCardView.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  onSuccess: PropTypes.func,
  changeView: PropTypes.func,
}

NewCardView.defaultProps = {
  onSuccess: () => {},
  changeView: () => {},
}

export default NewCardView
